/**
 * Run an OAuth-connect request and open the returned `authUrl` in a NEW TAB.
 *
 * The blank tab is opened synchronously (while the click's user-activation is
 * still valid) so popup blockers don't kill it; we then point it at the auth
 * URL once the API responds. Falls back to a same-tab redirect if the popup was
 * blocked, and closes the tab if the request fails or returns no URL.
 *
 * Note: call this from within the mutation/click path so `window.open` runs in
 * the user gesture — don't defer it to an async `onSuccess`.
 */
export function connectInNewTab<T extends { authUrl?: string } | undefined>(
  request: () => Promise<T>
): Promise<T> {
  const authWindow = window.open('', '_blank');
  return request()
    .then((data) => {
      const url = data?.authUrl;
      if (url) {
        if (authWindow && !authWindow.closed) authWindow.location.href = url;
        else window.location.href = url; // popup blocked -> same-tab fallback
      } else {
        authWindow?.close();
      }
      return data;
    })
    .catch((e) => {
      authWindow?.close();
      throw e;
    });
}
